import React, { useState, useRef, useEffect } from "react";
import { MdOutlineLogout, MdOutlineSettings } from "react-icons/md";
import { IoPersonOutline } from "react-icons/io5";
import { CgProfile } from "react-icons/cg";
import { IoMdNotificationsOutline } from "react-icons/io";
import { HiOutlineChevronRight } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import SearchBar from "./SearchBar";
import { useUser } from "../context/UserContext";

// ── animation presets ──────────────────────────────────────────
const dropdownVariants = {
  hidden: { opacity: 0, y: -8, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.18, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    y: -6,
    scale: 0.97,
    transition: { duration: 0.13, ease: "easeIn" },
  },
};

// ── tiny sub-components ────────────────────────────────────────

/** Pulsing dot badge */
function Badge({ count }) {
  if (!count) return null;
  return (
    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-60" />
      <span className="relative flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[9px] font-bold text-white leading-none">
        {count > 9 ? "9+" : count}
      </span>
    </span>
  );
}

/** Individual notification row */
function NotifItem({ icon, title, body, time, unread }) {
  return (
    <div
      className={`flex gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-colors ${
        unread ? "bg-indigo-50/70 hover:bg-indigo-100/60" : "hover:bg-gray-50"
      }`}
    >
      <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-base">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={`text-[13px] leading-snug truncate ${
            unread ? "font-semibold text-gray-800" : "text-gray-700"
          }`}
        >
          {title}
        </p>
        <p className="text-[11px] text-gray-400 truncate mt-0.5">{body}</p>
      </div>
      <span className="text-[10px] text-gray-400 flex-shrink-0 mt-0.5 whitespace-nowrap">
        {time}
      </span>
    </div>
  );
}

// ── main NavBar ────────────────────────────────────────────────
function NavBar() {
  const navigate = useNavigate();
  const { currentUser, loading, setCurrentUser } = useUser();
  const [showNotification, setShowNotification] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const notifRef = useRef();
  const profileRef = useRef();

  // Demo notifications – replace with real data
  const notifications = [
    {
      id: 1,
      icon: "🔔",
      title: "New purchase order #PO-2041",
      body: "Approved by Ravi Kumar",
      time: "2m",
      unread: true,
    },
    {
      id: 2,
      icon: "📄",
      title: "Invoice INV-0892 uploaded",
      body: "FinanceWatcher detected a new file",
      time: "18m",
      unread: true,
    },
    {
      id: 3,
      icon: "✅",
      title: "Drawing export completed",
      body: "Loop_Cabinet_A3.xlsx is ready",
      time: "1h",
      unread: false,
    },
  ];

  const unreadCount = notifications.filter((n) => n.unread).length;

  const handleLogout = () => {
    localStorage.removeItem("token");
    setCurrentUser(null);
    navigate("/login");
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target))
        setShowNotification(false);
      if (profileRef.current && !profileRef.current.contains(e.target))
        setShowProfile(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="h-16 flex items-center justify-between px-6">
        <motion.img
          src="/companylogo.png"
          alt="Company"
          className="h-10 w-auto"
        />
        <div className="flex items-center gap-4">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-9 w-9 rounded-full animate-pulse bg-slate-200"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-16 flex items-center justify-between px-6 relative bg-white border-b border-gray-100 shadow-sm">
      {/* Logo */}
      <motion.img
        src="/companylogo.png"
        alt="Company"
        className="h-10 w-auto cursor-pointer"
        whileHover={{ scale: 1.06 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      />

      {/* Right controls */}
      <div className="flex items-center gap-2">
        {/* Search */}
        <div className="sm:block hidden mr-2">
          <SearchBar />
        </div>

        {/* ── Notifications ── */}
        <div ref={notifRef} className="relative">
          <button
            onClick={() => {
              setShowNotification((s) => !s);
              setShowProfile(false);
            }}
            aria-label="Toggle notifications"
            className={`relative w-10 h-10 flex items-center justify-center rounded-xl transition-colors ${
              showNotification
                ? "bg-indigo-50 text-indigo-600"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            }`}
          >
            <IoMdNotificationsOutline className="text-[22px]" />
            <Badge count={unreadCount} />
          </button>

          <AnimatePresence>
            {showNotification && (
              <motion.div
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute right-0 mt-2 w-80 bg-white border border-gray-100 rounded-2xl shadow-2xl shadow-gray-200/70 overflow-hidden z-50"
              >
                {/* Header */}
                <div className="flex items-center justify-between px-4 pt-4 pb-2">
                  <h2 className="text-sm font-semibold text-gray-800">
                    Notifications
                  </h2>
                  {unreadCount > 0 && (
                    <span className="text-xs text-indigo-500 font-medium cursor-pointer hover:text-indigo-700">
                      Mark all read
                    </span>
                  )}
                </div>

                {/* List */}
                <div className="px-2 pb-2 flex flex-col gap-0.5 max-h-72 overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.map((n) => (
                      <NotifItem key={n.id} {...n} />
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                      <IoMdNotificationsOutline className="text-4xl mb-2 opacity-40" />
                      <p className="text-sm">All caught up!</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-100 px-4 py-2.5">
                  <button className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 font-medium w-full justify-center transition-colors">
                    View all notifications{" "}
                    <HiOutlineChevronRight className="text-sm" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Profile ── */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => {
              setShowProfile((s) => !s);
              setShowNotification(false);
            }}
            aria-label="Toggle profile menu"
            className={`relative w-10 h-10 flex items-center justify-center rounded-xl transition-colors ${
              showProfile
                ? "bg-indigo-50 text-indigo-600"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            }`}
          >
            {/* Show avatar initials if name available, else icon */}
            {currentUser?.name ? (
              <span className="w-7 h-7 rounded-full bg-indigo-600 text-white text-xs font-semibold flex items-center justify-center leading-none select-none">
                {currentUser.name
                  .split(" ")
                  .map((w) => w[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase()}
              </span>
            ) : (
              <CgProfile className="text-[22px]" />
            )}
          </button>

          <AnimatePresence>
            {showProfile && (
              <motion.div
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute right-0 mt-2 w-56 bg-white border border-gray-100 rounded-2xl shadow-2xl shadow-gray-200/70 overflow-hidden z-50"
              >
                {/* User info header */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {currentUser?.name || "My Account"}
                  </p>
                  <p className="text-xs text-gray-400 truncate mt-0.5">
                    {currentUser?.email || ""}
                  </p>
                </div>

                {/* Menu items */}
                <div className="p-1.5 flex flex-col gap-0.5">
                  <ProfileMenuItem
                    icon={<IoPersonOutline />}
                    label="Profile"
                    onClick={() => {
                      setShowProfile(false);
                      navigate("/admin/profile");
                    }}
                  />
                  <ProfileMenuItem
                    icon={<MdOutlineSettings />}
                    label="Settings"
                    onClick={() => {
                      setShowProfile(false);
                      navigate("/admin/settings");
                    }}
                  />
                </div>

                {/* Logout */}
                <div className="p-1.5 pt-0 border-t border-gray-100 mt-0.5">
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-sm text-red-500 hover:bg-red-50 transition-colors font-medium"
                  >
                    <MdOutlineLogout className="text-base" />
                    Log out
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/** Generic profile menu button */
function ProfileMenuItem({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-sm text-gray-700 hover:bg-gray-100 transition-colors"
    >
      <span className="text-base text-gray-500">{icon}</span>
      {label}
    </button>
  );
}

export default NavBar;